import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { ArrowRight, HelpCircle } from 'lucide-react';

const faqCategories = [
  {
    title: 'General Questions',
    faqs: [
      {
        question: 'What is QuickFinance?',
        answer: 'QuickFinance is a UK-based business finance broker that connects businesses with funding from £10,000 to £10 million. We work with a network of trusted lending partners to find the best funding solutions for your business needs.'
      },
      {
        question: 'Are you a lender or a broker?',
        answer: 'We are a credit broker, not a lender. This means we work with multiple funding partners to find the best rates and terms for your business. We may receive a commission from our funding partners for successful introductions.'
      },
      {
        question: 'Is QuickFinance regulated?',
        answer: 'Yes, we are authorised and regulated by the Financial Conduct Authority (FCA). This ensures we meet strict standards for consumer protection and fair treatment.'
      },
      {
        question: 'What types of businesses do you work with?',
        answer: 'We work with a wide range of businesses including limited companies, sole traders, and partnerships. Both start-ups and established businesses are welcome to apply.'
      }
    ]
  },
  {
    title: 'Application Process',
    faqs: [
      {
        question: 'How do I apply for funding?',
        answer: 'Simply complete our online application form with your business details and funding requirements. You\'ll receive an eligibility decision in as little as 4 hours during business days.'
      },
      {
        question: 'What documents do I need to apply?',
        answer: 'Our application process requires minimal paperwork. Typically, we\'ll need basic business information and may request bank statements or accounts depending on the loan amount and your circumstances.'
      },
      {
        question: 'How long does the application process take?',
        answer: 'You can receive an eligibility decision in as little as 4 hours. Once approved and you\'ve accepted the offer, funds can be released within 24 hours.'
      },
      {
        question: 'Will applying affect my credit score?',
        answer: 'Our initial eligibility check is a soft search, which doesn\'t affect your credit score. A full credit check is only performed once you decide to proceed with an application.'
      }
    ]
  },
  {
    title: 'Funding Options',
    faqs: [
      {
        question: 'How much can I borrow?',
        answer: 'We offer business funding from £10,000 up to £10 million. The amount you can borrow depends on your business circumstances, trading history, and the purpose of the loan.'
      },
      {
        question: 'What\'s the difference between secured and unsecured loans?',
        answer: 'Unsecured loans don\'t require collateral but may have higher interest rates. Secured loans are backed by assets (like property or equipment) and typically offer lower rates and higher borrowing limits.'
      },
      {
        question: 'What can I use the funding for?',
        answer: 'Our funding can be used for various purposes including working capital, business expansion, equipment purchase, VAT payments, marketing, stock purchase, and more.'
      },
      {
        question: 'What are the repayment terms?',
        answer: 'We offer flexible repayment terms tailored to your business needs. Terms typically range from 3 months to 5 years, with fixed monthly repayments so you always know what you\'re paying.'
      }
    ]
  },
  {
    title: 'Costs & Fees',
    faqs: [
      {
        question: 'What are the interest rates?',
        answer: 'Interest rates vary depending on your business profile, loan amount, and term length. Rates are determined by our funding partners and will be clearly disclosed before you accept any offer. Representative APR example: 9.9%.'
      },
      {
        question: 'Are there any hidden fees?',
        answer: 'No, we believe in complete transparency. All fees and charges will be clearly explained before you accept any funding offer. There are no hidden costs or surprises.'
      },
      {
        question: 'Can I repay early?',
        answer: 'Yes, early settlement discounts are available. If you wish to repay your loan early, you may be entitled to a reduction in the total amount owed. Terms vary by lender.'
      },
      {
        question: 'Do you charge application fees?',
        answer: 'No, there are no fees to apply or receive a quote from QuickFinance. You only pay if you decide to proceed with funding.'
      }
    ]
  }
];

export default function FAQPage() {
  const navigate = useNavigate();

  const handleApplyClick = () => {
    navigate('/#apply');
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="faq-page">
      <Header />

      {/* Hero */}
      <section className="py-24 sm:py-32 bg-white" data-testid="faq-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-blue-200">
            <HelpCircle size={16} />
            Help Center
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-950 tracking-tight leading-tight mb-6 font-['Outfit']">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Find answers to common questions about our business funding solutions. Can't find what you're looking for? Our team is here to help.
          </p>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-16 sm:py-24 bg-slate-50" data-testid="faq-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {faqCategories.map((category, catIndex) => (
            <div key={catIndex} className="mb-12" data-testid={`faq-category-${catIndex}`}>
              <h2 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-6 font-['Outfit']">
                {category.title}
              </h2>
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                <Accordion type="single" collapsible className="w-full">
                  {category.faqs.map((faq, faqIndex) => (
                    <AccordionItem key={faqIndex} value={`${catIndex}-${faqIndex}`} className="border-b border-slate-100 last:border-0">
                      <AccordionTrigger className="px-6 py-4 text-left hover:no-underline hover:bg-slate-50 transition-colors" data-testid={`faq-question-${catIndex}-${faqIndex}`}>
                        <span className="font-medium text-slate-900">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="px-6 pb-4 text-slate-600 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Representative Example */}
      <section className="py-16 bg-white" data-testid="representative-example">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-50 rounded-2xl p-6 sm:p-8 border border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4 font-['Outfit']">Representative Example</h3>
            <p className="text-slate-600 text-sm leading-relaxed mb-6">
              Amount of credit £10,000, duration 36 months, interest rate 9.9% per annum (fixed), representative APR 9.9%, total amount repayable £11,563, monthly repayment £321.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-xs text-slate-500 mb-1">Loan Amount</div>
                <div className="text-lg font-bold text-slate-900 font-['Outfit']">£10,000</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-xs text-slate-500 mb-1">Duration</div>
                <div className="text-lg font-bold text-slate-900 font-['Outfit']">36 months</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-xs text-slate-500 mb-1">APR</div>
                <div className="text-lg font-bold text-slate-900 font-['Outfit']">9.9%</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="text-xs text-slate-500 mb-1">Monthly Payment</div>
                <div className="text-lg font-bold text-slate-900 font-['Outfit']">£321</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-slate-900" data-testid="faq-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight mb-6 font-['Outfit']">
            Still Have Questions?
          </h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Our friendly UK-based team is available 6 days a week to answer any questions and guide you through the funding process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/contact')}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8" 
              data-testid="faq-contact-btn"
            >
              Contact Us <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              onClick={handleApplyClick}
              className="border-slate-600 text-white hover:bg-slate-800" 
              data-testid="faq-apply-btn"
            >
              Apply Now
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
